import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type Lang = 'en' | 'de';

const content = {
  en: {
    heroTitle: 'Youth Strength &',
    heroTitleAccent: 'Conditioning',
    heroTitleSub: 'Congress Munich',
    heroCongress: 'International Youth Congress · Munich 2026',
    heroTagline: '"The place where the best careers with children and youth begin."',
    heroScrollLabel: 'Discover more',
    heroLimitedLabel: 'Strictly limited to 220 attendees',
    heroCtaBtn: 'Secure My Spot',
    heroPills: ['München, Bavaria 🇩🇪', '220 Spots', '8 World-Class Speakers', 'NSCA Germany Cert.'],
    heroPillIcons: ['ri-map-pin-2-line', 'ri-group-line', 'ri-mic-line', 'ri-award-line'],
    heroDateLabel: 'Date',
    heroDateDay: '13 & 14',
    heroDateMonth: 'June 2026',
    saveTheDate: 'Save the Date',
    heroCertBadge: 'Exclusive Access Included',
    heroCertTitle: 'Get the world\'s leading certification in youth strength & conditioning',
    heroCertDesc: 'Attendees get exclusive access to the NSCA Germany Youth Strength & Conditioning Certificate — the most respected credential for working with children and youth. In English. Only available at this congress.',
    heroValueProps: [
      { icon: 'ri-mic-2-line', text: '8 world-class speakers over 2 days' },
      { icon: 'ri-award-fill', text: 'Official NSCA Germany certificate included' },
      { icon: 'ri-shield-star-line', text: 'Exclusive cert sale — only for attendees' },
      { icon: 'ri-group-3-line', text: 'Network with Europe\'s top youth coaches' },
    ],
    heroSubline: 'Two days. Eight experts. One certificate that changes everything.',

    formTitle: 'Register Your Interest',
    formSubtitle: 'Very limited spots — Be the first to know',
    soldOut: '1st Edition sold out in 10 days',
    labelName: 'Full Name',
    placeholderName: 'Your full name',
    labelEmail: 'Email',
    placeholderEmail: 'Your best email :)',
    checkLabel: 'I want to receive information about the',
    checkBold: 'Youth Strength & Conditioning Congress Munich',
    btnSubmit: 'Secure My Spot',
    btnSending: 'Sending...',
    successMsg: 'Please wait while we finish your registration...',
    errorMsg: 'Something went wrong. Please try again.',
    secure: '100% Secure',
    noSpam: 'No Spam',
    registrationSectionTitle: 'Youth Strength & Conditioning',
    registrationSectionSubtitle: 'The Congress That Transforms Your Career Through Scientific Evidence',
    registrationBadge: 'Official NSCA Germany Certificate',

    pricingTitle: 'Choose Your Pass',
    pricingSubtitle: 'Two days of world-class youth strength & conditioning. Limited to 220 spots.',
    pricingEarlyLabel: 'Early Bird',
    pricingMemberLabel: 'NSCA Germany Member',
    pricingStandardLabel: 'Standard',
    pricingEarlyPrice: '€169',
    pricingMemberPrice: '€149',
    pricingStandardPrice: '€199',
    pricingEarlyDesc: 'Until May 1st, 2026',
    pricingMemberDesc: 'Exclusive for NSCA Germany members',
    pricingStandardDesc: 'From May 1st until the event',
    pricingFeatures: [
      '2-day full access (June 13–14)',
      '8 world-class speakers',
      'Official NSCA Germany certificate',
      'Printed congress materials',
      'Networking lunch both days',
      'Access to recorded sessions',
    ],
    pricingMemberFeatures: [
      '2-day full access (June 13–14)',
      '8 world-class speakers',
      'Official NSCA Germany certificate',
      'Printed congress materials',
      '🍽️ Networking lunch included both days',
      'Access to recorded sessions',
    ],
    pricingCta: 'Secure My Spot',
    pricingNote: 'Spots are strictly limited to 220 attendees.',
    pricingLabel: 'Pricing',
    pricingBestValue: 'Best Value',
    pricingDeadline: '⏰ Early Bird ends May 1st',

    speakersTitle: 'Meet the Speakers',
    speakersSubtitle: '8 world-leading experts in youth strength & conditioning — two days, one stage.',
    speakerRevealLabel: 'To be revealed',
    speakersLabel: 'Speakers',
    speakersCta: 'Secure your spot before all 8 speakers are revealed — and prices go up.',
    speakersBtn: 'Secure My Spot',
    speakers: [
      { name: 'Des Ryan', role: 'Head of Athletic Development, Arsenal FC Academy', country: '🇮🇪', revealed: true, img: 'https://public.readdy.ai/ai/img_res/fab78c107cc434146f383eeb39ef89c2.jpg' },
      { name: 'Urs Granacher', role: 'Professor of Training & Movement Science, University of Freiburg', country: '🇩🇪', revealed: true, img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20distinguished%20male%20professor%20sports%20science%20university%20researcher%20in%20his%2050s%2C%20clean%20white%20studio%20background%2C%20academic%20look%2C%20confident%20expression%2C%20high%20resolution%20portrait%20photography&width=400&height=400&seq=speaker-urs-granacher-001&orientation=squarish' },
      { name: 'Avery Faigenbaum', role: 'PhD · The World Reference in Pediatric Strength', country: '🇺🇸', revealed: true, img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20distinguished%20male%20PhD%20professor%20pediatric%20exercise%20science%20in%20his%2060s%2C%20clean%20white%20studio%20background%2C%20authoritative%20warm%20expression%2C%20suit%2C%20high%20resolution%20portrait%20photography&width=400&height=400&seq=speaker-avery-faigenbaum-001&orientation=squarish' },
      { name: '???', role: 'Speaker #4', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Speaker #5', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Speaker #6', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Speaker #7', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Speaker #8', country: '🌍', revealed: false, img: '' },
    ],
  },
  de: {
    heroTitle: 'Youth Strength &',
    heroTitleAccent: 'Conditioning',
    heroTitleSub: 'Kongress München',
    heroCongress: 'Internationaler Jugendkongress · München 2026',
    heroTagline: '"Der Ort, an dem die besten Karrieren mit Kindern und Jugendlichen beginnen."',
    heroScrollLabel: 'Mehr entdecken',
    heroLimitedLabel: 'Streng begrenzt auf 220 Teilnehmer',
    heroCtaBtn: 'Meinen Platz sichern',
    heroPills: ['München, Bayern 🇩🇪', '220 Plätze', '8 Weltklasse-Referenten', 'NSCA Germany Zert.'],
    heroPillIcons: ['ri-map-pin-2-line', 'ri-group-line', 'ri-mic-line', 'ri-award-line'],
    heroDateLabel: 'Datum',
    heroDateDay: '13 & 14',
    heroDateMonth: 'Juni 2026',
    saveTheDate: 'Save the Date',
    heroCertBadge: 'Exklusiver Zugang inklusive',
    heroCertTitle: 'Erhalte die weltweit führende Zertifizierung im Jugendkrafttraining',
    heroCertDesc: 'Teilnehmer erhalten exklusiven Zugang zum NSCA Germany Youth Strength & Conditioning Zertifikat — die angesehenste Qualifikation für die Arbeit mit Kindern und Jugendlichen. Auf Englisch. Nur bei diesem Kongress erhältlich.',
    heroValueProps: [
      { icon: 'ri-mic-2-line', text: '8 Weltklasse-Referenten über 2 Tage' },
      { icon: 'ri-award-fill', text: 'Offizielles NSCA Germany Zertifikat inklusive' },
      { icon: 'ri-shield-star-line', text: 'Exklusiver Zertifikatsverkauf — nur für Teilnehmer' },
      { icon: 'ri-group-3-line', text: 'Netzwerk mit Europas Top-Jugendtrainern' },
    ],
    heroSubline: 'Zwei Tage. Acht Experten. Ein Zertifikat, das alles verändert.',

    formTitle: 'Interesse anmelden',
    formSubtitle: 'Sehr begrenzte Plätze — Sei der Erste, der es erfährt',
    soldOut: '1. Ausgabe in 10 Tagen ausverkauft',
    labelName: 'Vollständiger Name',
    placeholderName: 'Dein vollständiger Name',
    labelEmail: 'E-Mail',
    placeholderEmail: 'Deine beste E-Mail :)',
    checkLabel: 'Ich möchte Informationen über den',
    checkBold: 'Youth Strength & Conditioning Kongress München erhalten',
    btnSubmit: 'Meinen Platz sichern',
    btnSending: 'Wird gesendet...',
    successMsg: 'Bitte warte, während wir deine Anmeldung abschließen...',
    errorMsg: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    secure: '100% Sicher',
    noSpam: 'Kein Spam',
    registrationSectionTitle: 'Youth Strength & Conditioning',
    registrationSectionSubtitle: 'Der Kongress, der deine Karriere durch wissenschaftliche Evidenz transformiert',
    registrationBadge: 'Offizielles NSCA Germany Zertifikat',

    pricingTitle: 'Wähle deinen Pass',
    pricingSubtitle: 'Zwei Tage Weltklasse-Jugendkrafttraining. Begrenzt auf 220 Plätze.',
    pricingEarlyLabel: 'Early Bird',
    pricingMemberLabel: 'NSCA Germany Mitglied',
    pricingStandardLabel: 'Standard',
    pricingEarlyPrice: '169 €',
    pricingMemberPrice: '149 €',
    pricingStandardPrice: '199 €',
    pricingEarlyDesc: 'Bis 1. Mai 2026',
    pricingMemberDesc: 'Exklusiv für NSCA Germany Mitglieder',
    pricingStandardDesc: 'Ab 1. Mai bis zum Event',
    pricingFeatures: [
      '2-Tage-Vollzugang (13.–14. Juni)',
      '8 Weltklasse-Referenten',
      'Offizielles NSCA Germany Zertifikat',
      'Gedruckte Kongressmaterialien',
      'Networking-Mittagessen an beiden Tagen',
      'Zugang zu aufgezeichneten Sitzungen',
    ],
    pricingMemberFeatures: [
      '2-Tage-Vollzugang (13.–14. Juni)',
      '8 Weltklasse-Referenten',
      'Offizielles NSCA Germany Zertifikat',
      'Gedruckte Kongressmaterialien',
      '🍽️ Mittagessen an beiden Tagen inklusive',
      'Zugang zu aufgezeichneten Sitzungen',
    ],
    pricingCta: 'Meinen Platz sichern',
    pricingNote: 'Streng begrenzt auf 220 Teilnehmer.',
    pricingLabel: 'Preise',
    pricingBestValue: 'Bestes Angebot',
    pricingDeadline: '⏰ Early Bird endet am 1. Mai',

    speakersTitle: 'Die Referenten',
    speakersSubtitle: '8 weltweit führende Experten für Jugendkrafttraining — zwei Tage, eine Bühne.',
    speakerRevealLabel: 'Wird noch enthüllt',
    speakersLabel: 'Referenten',
    speakersCta: 'Sichere deinen Platz, bevor alle 8 Referenten enthüllt werden — und die Preise steigen.',
    speakersBtn: 'Meinen Platz sichern',
    speakers: [
      { name: 'Des Ryan', role: 'Head of Athletic Development, Arsenal FC Academy', country: '🇮🇪', revealed: true, img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20confident%20male%20sports%20scientist%20athletic%20development%20coach%20in%20his%2040s%2C%20clean%20white%20studio%20background%2C%20sharp%20suit%2C%20warm%20smile%2C%20high%20resolution%20portrait%20photography&width=400&height=400&seq=speaker-des-ryan-001&orientation=squarish' },
      { name: 'Urs Granacher', role: 'Professor für Trainings- und Bewegungswissenschaft, Universität Freiburg', country: '🇩🇪', revealed: true, img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20distinguished%20male%20professor%20sports%20science%20university%20researcher%20in%20his%2050s%2C%20clean%20white%20studio%20background%2C%20academic%20look%2C%20confident%20expression%2C%20high%20resolution%20portrait%20photography&width=400&height=400&seq=speaker-urs-granacher-001&orientation=squarish' },
      { name: 'Avery Faigenbaum', role: 'PhD · Die Weltautorität im pädiatrischen Krafttraining', country: '🇺🇸', revealed: true, img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20distinguished%20male%20PhD%20professor%20pediatric%20exercise%20science%20in%20his%2060s%2C%20clean%20white%20studio%20background%2C%20authoritative%20warm%20expression%2C%20suit%2C%20high%20resolution%20portrait%20photography&width=400&height=400&seq=speaker-avery-faigenbaum-001&orientation=squarish' },
      { name: '???', role: 'Referent #4', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Referent #5', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Referent #6', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Referent #7', country: '🌍', revealed: false, img: '' },
      { name: '???', role: 'Referent #8', country: '🌍', revealed: false, img: '' },
    ],
  },
};

// ─── Language Selector ────────────────────────────────────────────────────────
function LangSelector({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-end gap-2">
        <span className="text-xs text-gray-400 font-medium mr-1">Language:</span>
        <button
          onClick={() => setLang('en')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${lang === 'en' ? 'bg-gray-900 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
        >
          <span>🇬🇧</span> English
        </button>
        <button
          onClick={() => setLang('de')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${lang === 'de' ? 'bg-gray-900 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
        >
          <span>🇩🇪</span> Deutsch
        </button>
      </div>
    </div>
  );
}

// ─── Save the Date Hero ───────────────────────────────────────────────────────
function SaveTheDateHero({ lang, onScrollToForm }: { lang: Lang; onScrollToForm: () => void }) {
  const t = content[lang];
  const [scrollY, setScrollY] = useState(0);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Entrance animation trigger
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Countdown to June 13, 2026
  useEffect(() => {
    const target = new Date('2026-06-13T09:00:00').getTime();
    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) return;
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section className="relative w-full overflow-hidden" style={{ minHeight: '100vh' }}>
      {/* Keyframe styles */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes floatA {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.18; }
          33%       { transform: translateY(-28px) translateX(14px); opacity: 0.32; }
          66%       { transform: translateY(14px) translateX(-10px); opacity: 0.22; }
        }
        @keyframes floatB {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.12; }
          40%       { transform: translateY(22px) translateX(-18px); opacity: 0.28; }
          70%       { transform: translateY(-16px) translateX(12px); opacity: 0.16; }
        }
        @keyframes floatC {
          0%, 100% { transform: translateY(0px); opacity: 0.10; }
          50%       { transform: translateY(-20px); opacity: 0.22; }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(209,177,104,0.0), 0 8px 40px rgba(209,177,104,0.35); }
          50%       { box-shadow: 0 0 0 12px rgba(209,177,104,0.12), 0 8px 60px rgba(209,177,104,0.55); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes countFlip {
          0%   { transform: translateY(-6px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .hero-fade-up-1 { animation: fadeUp 0.8s cubic-bezier(.22,1,.36,1) 0.1s both; }
        .hero-fade-up-2 { animation: fadeUp 0.8s cubic-bezier(.22,1,.36,1) 0.25s both; }
        .hero-fade-up-3 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.4s both; }
        .hero-fade-up-4 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.55s both; }
        .hero-fade-up-5 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.7s both; }
        .hero-fade-up-6 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.85s both; }
        .hero-fade-up-7 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 1.0s both; }
        .hero-fade-up-8 { animation: fadeUp 0.9s cubic-bezier(.22,1,.36,1) 1.15s both; }
        .hero-cta-glow  { animation: pulseGlow 2.4s ease-in-out infinite; }
        .shimmer-text {
          background: linear-gradient(90deg, #d1b168 0%, #f0d080 40%, #fff8e0 50%, #f0d080 60%, #d1b168 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3.5s linear infinite;
        }
      `}</style>

      {/* Background image with parallax */}
      <img
        src="https://readdy.ai/api/search-image?query=Munich%20city%20skyline%20at%20golden%20hour%2C%20Marienplatz%20cathedral%20towers%2C%20dramatic%20sky%20with%20warm%20orange%20and%20amber%20light%2C%20aerial%20panoramic%20view%2C%20cinematic%20photography%2C%20ultra%20high%20resolution%2C%20architectural%20beauty%2C%20Bavaria%20Germany&width=1920&height=1080&seq=munich-hero-001&orientation=landscape"
        alt="Munich Congress Venue"
        className="absolute inset-0 w-full h-full object-cover object-top"
        style={{ transform: `translateY(${scrollY * 0.3}px)`, willChange: 'transform' }}
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/90"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40"></div>

      {/* ── Floating particles ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gold orbs */}
        <div style={{ position:'absolute', top:'12%', left:'8%', width:180, height:180, borderRadius:'50%', background:'radial-gradient(circle, rgba(209,177,104,0.18) 0%, transparent 70%)', animation:'floatA 9s ease-in-out infinite' }}></div>
        <div style={{ position:'absolute', top:'55%', left:'5%', width:120, height:120, borderRadius:'50%', background:'radial-gradient(circle, rgba(209,177,104,0.14) 0%, transparent 70%)', animation:'floatB 12s ease-in-out infinite 2s' }}></div>
        <div style={{ position:'absolute', top:'20%', right:'7%', width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle, rgba(209,177,104,0.12) 0%, transparent 70%)', animation:'floatC 10s ease-in-out infinite 1s' }}></div>
        <div style={{ position:'absolute', top:'70%', right:'10%', width:140, height:140, borderRadius:'50%', background:'radial-gradient(circle, rgba(209,177,104,0.16) 0%, transparent 70%)', animation:'floatA 14s ease-in-out infinite 3s' }}></div>
        <div style={{ position:'absolute', top:'40%', left:'50%', width:90, height:90, borderRadius:'50%', background:'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', animation:'floatB 8s ease-in-out infinite 0.5s' }}></div>
        {/* Decorative lines */}
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#d1b168]/15 to-transparent"></div>
        <div className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#d1b168]/10 to-transparent"></div>
        {/* Small sparkle dots */}
        {[
          { top:'18%', left:'22%', size:4, delay:'0s' },
          { top:'35%', left:'78%', size:3, delay:'1.2s' },
          { top:'62%', left:'15%', size:5, delay:'2.1s' },
          { top:'75%', left:'65%', size:3, delay:'0.7s' },
          { top:'48%', left:'88%', size:4, delay:'1.8s' },
          { top:'28%', left:'45%', size:3, delay:'3s' },
        ].map((dot, i) => (
          <div key={i} style={{
            position:'absolute', top: dot.top, left: dot.left,
            width: dot.size, height: dot.size, borderRadius:'50%',
            background:'rgba(209,177,104,0.7)',
            animation:`floatC 6s ease-in-out infinite ${dot.delay}`,
          }}></div>
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-20 text-center w-full">

        {/* Save the Date badge */}
        <div className="flex items-center gap-3 mb-6 hero-fade-up-1">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#d1b168]/60"></div>
          <div className="flex items-center gap-2 bg-[#d1b168]/15 border border-[#d1b168]/50 backdrop-blur-sm rounded-full px-6 py-2.5">
            <i className="ri-calendar-event-fill text-[#d1b168] text-sm"></i>
            <span className="text-[#d1b168] text-xs font-black uppercase tracking-[0.3em]">{t.saveTheDate}</span>
          </div>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#d1b168]/60"></div>
        </div>

        <p className="text-white/50 text-xs font-bold uppercase tracking-[0.4em] mb-5 hero-fade-up-1">{t.heroCongress}</p>

        {/* Main title */}
        <h1 className="text-5xl lg:text-8xl font-black text-white leading-none mb-4 tracking-tight drop-shadow-2xl w-full hero-fade-up-2">
          {t.heroTitle}
          <br />
          <span className="shimmer-text">
            {t.heroTitleAccent}
          </span>
          <br />
          <span className="text-white/85 text-3xl lg:text-5xl font-light tracking-widest">
            {t.heroTitleSub}
          </span>
        </h1>

        {/* Subline */}
        <p className="text-white/60 text-base lg:text-lg font-light max-w-lg mx-auto mb-8 italic hero-fade-up-3">
          {t.heroSubline}
        </p>

        {/* Date block */}
        <div className="flex items-center justify-center gap-4 mb-6 hero-fade-up-3">
          <div className="h-px w-12 bg-white/25"></div>
          <div className="bg-white/10 backdrop-blur-md border border-white/25 rounded-2xl px-8 py-4 text-center">
            <div className="text-[#d1b168] text-xs font-black uppercase tracking-widest mb-1">{t.heroDateLabel}</div>
            <div className="text-white font-black text-3xl lg:text-4xl tracking-tight">{t.heroDateDay}</div>
            <div className="text-white/80 font-semibold text-lg">{t.heroDateMonth}</div>
          </div>
          <div className="h-px w-12 bg-white/25"></div>
        </div>

        {/* ── Countdown ── */}
        <div className="hero-fade-up-4 mb-8">
          <p className="text-white/35 text-xs uppercase tracking-widest mb-3 font-semibold">
            {lang === 'de' ? 'Countdown zum Event' : 'Countdown to the event'}
          </p>
          <div className="flex items-center justify-center gap-3">
            {[
              { val: countdown.days,    label: lang === 'de' ? 'Tage' : 'Days' },
              { val: countdown.hours,   label: lang === 'de' ? 'Std' : 'Hrs' },
              { val: countdown.minutes, label: lang === 'de' ? 'Min' : 'Min' },
              { val: countdown.seconds, label: lang === 'de' ? 'Sek' : 'Sec' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center">
                <div
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center font-black text-white"
                  style={{ width: 64, height: 64, fontSize: 28, letterSpacing: '-0.02em' }}
                >
                  <span key={item.val} style={{ animation: 'countFlip 0.3s ease both' }}>
                    {pad(item.val)}
                  </span>
                </div>
                <span className="text-white/35 text-xs mt-1.5 font-semibold uppercase tracking-wider">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info pills */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10 hero-fade-up-5">
          {t.heroPills.map((pill, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2">
              <i className={`${t.heroPillIcons[i]} text-[#d1b168] text-sm`}></i>
              <span className="text-white font-semibold text-sm">{pill}</span>
            </div>
          ))}
        </div>

        {/* ★ Certificate highlight block */}
        <div className="w-full max-w-2xl mx-auto mb-10 hero-fade-up-6">
          <div className="relative bg-white/8 backdrop-blur-md border border-[#d1b168]/40 rounded-2xl p-6 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#d1b168]/10 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px w-8 bg-[#d1b168]/50"></div>
              <span className="text-[#d1b168] text-xs font-black uppercase tracking-widest">{t.heroCertBadge}</span>
              <div className="h-px w-8 bg-[#d1b168]/50"></div>
            </div>
            <p className="text-white font-black text-lg lg:text-xl mb-2 leading-snug">{t.heroCertTitle}</p>
            <p className="text-white/60 text-sm leading-relaxed">{t.heroCertDesc}</p>
          </div>
        </div>

        {/* Value props row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto mb-10 hero-fade-up-6">
          {t.heroValueProps.map((vp, i) => (
            <div key={i} className="flex flex-col items-center gap-2 bg-white/6 backdrop-blur-sm border border-white/12 rounded-xl px-3 py-4">
              <div className="w-9 h-9 flex items-center justify-center bg-[#d1b168]/20 rounded-full">
                <i className={`${vp.icon} text-[#d1b168] text-base`}></i>
              </div>
              <span className="text-white/75 text-xs text-center leading-snug font-medium">{vp.text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="hero-fade-up-7">
          <button
            onClick={onScrollToForm}
            className="hero-cta-glow group flex items-center gap-3 bg-gradient-to-r from-[#d1b168] to-[#c4a158] text-white font-black text-lg px-10 py-5 rounded-full hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap mb-3"
          >
            <i className="ri-lock-line text-xl"></i>
            {t.heroCtaBtn}
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>

        <p className="text-white/35 text-xs mb-10 hero-fade-up-8">{t.heroLimitedLabel}</p>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/30 text-xs uppercase tracking-widest">{t.heroScrollLabel}</span>
          <i className="ri-arrow-down-line text-white/30 text-xl"></i>
        </div>
      </div>
    </section>
  );
}

// ─── Registration Section ─────────────────────────────────────────────────────
function RegistrationSection({
  lang,
  registroRef,
  formData,
  setFormData,
  isSubmitting,
  submitStatus,
  onSubmit,
}: {
  lang: Lang;
  registroRef: React.RefObject<HTMLDivElement>;
  formData: { nombre: string; email: string; check: boolean };
  setFormData: (d: { nombre: string; email: string; check: boolean }) => void;
  isSubmitting: boolean;
  submitStatus: 'idle' | 'success' | 'error';
  onSubmit: (e: React.FormEvent) => void;
}) {
  const t = content[lang];

  return (
    <section
      ref={registroRef}
      className="relative py-16 px-4 lg:py-20 lg:px-6 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)' }}
    >
      {/* Subtle gold accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#d1b168]/8 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#d1b168]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#d1b168]/15 border border-[#d1b168]/40 rounded-full px-4 py-2 mb-4">
            <i className="ri-fire-fill text-white text-sm"></i>
            <span className="text-[#d1b168] text-xs font-bold uppercase tracking-widest">{t.registrationBadge}</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-black text-white leading-tight mb-3">{t.registrationSectionTitle}</h2>
          <p className="text-lg text-[#d1b168] font-semibold">{t.registrationSectionSubtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Image */}
          <div className="order-1" style={{ aspectRatio: '1 / 1' }}>
            <div className="relative rounded-2xl overflow-hidden w-full h-full shadow-2xl">
              <div className="absolute -inset-4 bg-[#d1b168]/15 rounded-3xl blur-2xl z-0"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/0aa41ded-89ca-4e2a-97a3-d8937e903601.png"
                alt="Young athletes NSCA Germany YSCC"
                className="relative z-10 w-full h-full object-cover object-top"
              />
              {/* Overlay badge */}
              <div className="absolute bottom-4 left-4 right-4 z-20">
                <div className="bg-black/60 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#d1b168] rounded-full flex-shrink-0">
                    <i className="ri-time-line text-white text-sm"></i>
                  </div>
                  <span className="text-white text-xs font-bold">{t.soldOut}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="order-2 bg-white rounded-2xl shadow-2xl p-6 lg:p-8 flex flex-col justify-center" style={{ aspectRatio: '1 / 1' }}>
            <div className="text-center mb-6">
              <h3 className="text-xl lg:text-2xl font-black text-gray-900 mb-1">{t.formTitle}</h3>
              <p className="text-gray-500 text-sm">{t.formSubtitle}</p>
            </div>
            <form id={`form-registro-${lang}`} data-readdy-form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor={`nombre-${lang}`} className="block text-sm font-semibold text-gray-700 mb-1.5">{t.labelName}</label>
                <input
                  type="text"
                  id={`nombre-${lang}`}
                  name="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
                  placeholder={t.placeholderName}
                />
              </div>
              <div>
                <label htmlFor={`email-${lang}`} className="block text-sm font-semibold text-gray-700 mb-1.5">{t.labelEmail}</label>
                <input
                  type="email"
                  id={`email-${lang}`}
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
                  placeholder={t.placeholderEmail}
                />
              </div>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id={`check-${lang}`}
                  name="quiero_informacion"
                  checked={formData.check}
                  onChange={(e) => setFormData({ ...formData, check: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-gray-900 cursor-pointer flex-shrink-0"
                />
                <label htmlFor={`check-${lang}`} className="text-xs text-gray-600 cursor-pointer leading-snug">
                  {t.checkLabel} <strong className="text-gray-900">{t.checkBold}</strong>
                </label>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#d1b168] to-[#c4a158] text-white font-black py-4 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm flex items-center justify-center gap-2"
              >
                {isSubmitting
                  ? <><i className="ri-loader-4-line animate-spin"></i>{t.btnSending}</>
                  : <><i className="ri-lock-line"></i>{t.btnSubmit}<i className="ri-arrow-right-line"></i></>
                }
              </button>
              {submitStatus === 'success' && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-xs font-semibold flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 flex-shrink-0"></i>{t.successMsg}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs">{t.errorMsg}</div>
              )}
            </form>
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1"><i className="ri-lock-line"></i><span>{t.secure}</span></div>
              <div className="flex items-center gap-1"><i className="ri-shield-check-line"></i><span>{t.noSpam}</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing Section ──────────────────────────────────────────────────────────
function PricingSection({ lang, onScrollToForm }: { lang: Lang; onScrollToForm: () => void }) {
  const t = content[lang];

  const plans = [
    {
      label: t.pricingMemberLabel,
      price: t.pricingMemberPrice,
      desc: t.pricingMemberDesc,
      icon: 'ri-vip-crown-line',
      highlight: false,
      best: false,
      accentClass: 'border-gray-200 bg-white',
      btnClass: 'bg-gray-800 text-white hover:bg-gray-700',
      badgeClass: 'bg-gray-100 text-gray-500',
      priceClass: 'text-gray-800',
      features: t.pricingMemberFeatures,
      deadline: null,
    },
    {
      label: t.pricingEarlyLabel,
      price: t.pricingEarlyPrice,
      desc: t.pricingEarlyDesc,
      icon: 'ri-flashlight-line',
      highlight: true,
      best: true,
      accentClass: 'border-[#d1b168] bg-white',
      btnClass: 'bg-gradient-to-r from-[#d1b168] to-[#c4a158] text-white hover:shadow-lg',
      badgeClass: 'bg-[#d1b168]/15 text-[#c4a158] border border-[#d1b168]/40',
      priceClass: 'text-gray-900',
      features: t.pricingFeatures,
      deadline: t.pricingDeadline,
    },
    {
      label: t.pricingStandardLabel,
      price: t.pricingStandardPrice,
      desc: t.pricingStandardDesc,
      icon: 'ri-ticket-line',
      highlight: false,
      best: false,
      disabled: true,
      accentClass: 'border-gray-200 bg-gray-50 opacity-60',
      btnClass: 'bg-gray-300 text-gray-400 cursor-not-allowed',
      badgeClass: 'bg-gray-100 text-gray-400',
      priceClass: 'text-gray-400',
      features: t.pricingFeatures,
      deadline: null,
    },
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 mb-5">
            <i className="ri-price-tag-3-line text-gray-500 text-sm"></i>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">{t.pricingLabel}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">{t.pricingTitle}</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">{t.pricingSubtitle}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative rounded-2xl border-2 shadow-lg overflow-hidden transition-all duration-300 ${plan.disabled ? '' : 'hover:-translate-y-1 hover:shadow-xl'} ${plan.accentClass}`}
            >
              {plan.highlight && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#d1b168] to-[#c4a158]"></div>
              )}
              {plan.best && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs font-black uppercase tracking-wider bg-[#d1b168] text-white px-3 py-1 rounded-full">
                    {t.pricingBestValue}
                  </span>
                </div>
              )}
              {plan.disabled && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs font-black uppercase tracking-wider bg-gray-300 text-gray-500 px-3 py-1 rounded-full">
                    {lang === 'de' ? 'Ab 1. Mai' : 'From May 1st'}
                  </span>
                </div>
              )}
              <div className="p-7">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 mb-4 text-xs font-black uppercase tracking-wider ${plan.badgeClass}`}>
                  <i className={`${plan.icon} text-xs`}></i>
                  {plan.label}
                </div>
                <div className={`text-5xl font-black mb-1 ${plan.priceClass}`}>{plan.price}</div>
                <div className="text-gray-400 text-sm mb-2">{plan.desc}</div>
                {plan.deadline && (
                  <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-4">
                    <span className="text-amber-700 text-xs font-bold">{plan.deadline}</span>
                  </div>
                )}
                <ul className="space-y-2.5 mb-7 mt-2">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-gray-600 text-sm">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#d1b168] text-sm"></i>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={plan.disabled ? undefined : onScrollToForm}
                  disabled={!!plan.disabled}
                  className={`w-full font-black py-3.5 rounded-xl transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2 text-sm ${plan.disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : `cursor-pointer hover:scale-[1.02] ${plan.btnClass}`}`}
                >
                  <i className={plan.disabled ? 'ri-time-line' : 'ri-lock-line'}></i>
                  {plan.disabled ? (lang === 'de' ? 'Ab 1. Mai verfügbar' : 'Available from May 1st') : t.pricingCta}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs">{t.pricingNote}</p>
      </div>
    </section>
  );
}

// ─── Speakers Section ─────────────────────────────────────────────────────────
function SpeakersSection({ lang, onScrollToForm }: { lang: Lang; onScrollToForm: () => void }) {
  const t = content[lang];
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section className="py-24 px-6" style={{ background: '#f7f6f4' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 mb-5 shadow-sm">
            <i className="ri-mic-2-line text-gray-500 text-sm"></i>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">{t.speakersLabel}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">{t.speakersTitle}</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">{t.speakersSubtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
          {t.speakers.map((speaker, i) => (
            <div
              key={i}
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${speaker.revealed ? 'shadow-md hover:shadow-2xl hover:-translate-y-1.5' : 'shadow-sm'}`}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {speaker.revealed ? (
                <>
                  <div className="aspect-square bg-gray-200 overflow-hidden">
                    <img
                      src={speaker.img}
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500"
                      style={{ transform: hoveredIdx === i ? 'scale(1.06)' : 'scale(1)' }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-lg mb-0.5">{speaker.country}</div>
                    <div className="text-white font-black text-sm leading-tight">{speaker.name}</div>
                    <div className="text-white/65 text-xs leading-snug mt-1">{speaker.role}</div>
                  </div>
                  <div className={`absolute inset-0 bg-[#d1b168]/8 transition-opacity duration-300 ${hoveredIdx === i ? 'opacity-100' : 'opacity-0'}`}></div>
                </>
              ) : (
                <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 flex flex-col items-center justify-center p-4 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-pulse"></div>
                  <div className="w-14 h-14 flex items-center justify-center bg-white/40 rounded-full mb-3 backdrop-blur-sm">
                    <i className="ri-question-mark text-gray-400 text-2xl"></i>
                  </div>
                  <div className="text-gray-500 font-black text-2xl mb-2">???</div>
                  <div className="inline-flex items-center gap-1 bg-white/60 rounded-full px-3 py-1">
                    <i className="ri-lock-line text-gray-400 text-xs"></i>
                    <span className="text-gray-500 text-xs font-semibold">{t.speakerRevealLabel}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-500 text-sm mb-6">{t.speakersCta}</p>
          <button
            onClick={onScrollToForm}
            className="group inline-flex items-center gap-3 bg-gray-900 text-white font-black text-base px-8 py-4 rounded-full hover:bg-gray-700 hover:shadow-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-lock-line"></i>
            {t.speakersBtn}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PruebaEnIngles() {
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('en');
  const [formData, setFormData] = useState({ nombre: '', email: '', check: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const registroRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    registroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    setSubmitStatus('idle');
  }, [lang]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-registro-en';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'Youth Strength & Conditioning Congress Munich 2026',
      description: 'International congress on youth strength and conditioning, June 13-14 2026, Munich. NSCA Spain official certificate.',
      startDate: '2026-06-13',
      endDate: '2026-06-14',
      location: { '@type': 'Place', name: 'München', address: { '@type': 'PostalAddress', addressLocality: 'Munich', addressCountry: 'DE' } },
      organizer: { '@type': 'Organization', name: 'NSCA Spain' },
      offers: [
        { '@type': 'Offer', price: '149', priceCurrency: 'EUR', name: 'NSCA Member' },
        { '@type': 'Offer', price: '169', priceCurrency: 'EUR', name: 'Early Bird', availability: 'https://schema.org/LimitedAvailability' },
        { '@type': 'Offer', price: '199', priceCurrency: 'EUR', name: 'Standard' },
      ],
      maximumAttendeeCapacity: 220,
    });
    document.head.appendChild(script);
    return () => { const existing = document.getElementById('schema-registro-en'); if (existing) existing.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: formData.nombre, email: formData.email, quiero_informacion: formData.check, formulario: `hero-${lang}` }),
      });
      if (response.ok) {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead', { content_name: 'Youth Strength Congress Munich' });
        }
        setSubmitStatus('success');
        setTimeout(() => { navigate('/gracias'); }, 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <LangSelector lang={lang} setLang={setLang} />
      <SaveTheDateHero lang={lang} onScrollToForm={scrollToForm} />
      <RegistrationSection
        lang={lang}
        registroRef={registroRef as React.RefObject<HTMLDivElement>}
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        submitStatus={submitStatus}
        onSubmit={handleSubmit}
      />
      <PricingSection lang={lang} onScrollToForm={scrollToForm} />
      <SpeakersSection lang={lang} onScrollToForm={scrollToForm} />
    </div>
  );
}
